/**
 * Consonant Practice Page
 *
 * Dedicated page for consonant letter practice with physical cards.
 * Accessible via /consonant-practice route.
 *
 * Flow:
 * 1. Intro screen with instructions + recording toggle
 * 2. Guided 6-prompt scanning session
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelectedChild } from '@/contexts/ChildContext';
import { useParentStatus } from '@/hooks/useParentStatus';
import { PageSpinner } from '@/components/ui/Spinner';
import {
  ConsonantPracticeIntro,
  ConsonantPracticeSession
} from '@/components/consonant-practice';
import { ensureAudioReady } from '@/lib/voice-agent/audio-engine';
import { useTranslation } from '@/lib/i18n';

// ============================================================================
// Types
// ============================================================================

type ViewState = 'intro' | 'session';

// ============================================================================
// Main Component
// ============================================================================

export const ConsonantPracticePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedChild, isLoading } = useSelectedChild();
  const parentStatus = useParentStatus({ childId: selectedChild?.id });

  const [viewState, setViewState] = useState<ViewState>('intro');
  const [recordingEnabled, setRecordingEnabled] = useState(false);

  if (isLoading) {
    return <PageSpinner />;
  }

  if (!parentStatus.isLoading && !parentStatus.canAccessConsonant) {
    navigate('/');
    return null;
  }

  if (!selectedChild) {
    return (
      <div className="flex items-center justify-center p-4 min-h-[40vh]">
        <div className="text-center">
          <p className="text-[#636366] mb-4">{t('Please add a child in Settings to track progress.')}</p>
          <button
            onClick={() => navigate('/settings')}
            className="px-4 py-2 bg-[#5C2D91] text-white rounded-xl text-sm"
          >
            {t('Go to Settings')}
          </button>
        </div>
      </div>
    );
  }

  if (viewState === 'intro') {
    return (
      <ConsonantPracticeIntro
        childName={selectedChild?.name || 'your child'}
        onStartSession={(recording) => {
          setRecordingEnabled(recording);
          ensureAudioReady();
          setViewState('session');
        }}
      />
    );
  }

  return (
    <ConsonantPracticeSession
      childId={selectedChild?.id}
      childName={selectedChild?.name || 'your child'}
      recordingEnabled={recordingEnabled}
      onEndSession={() => setViewState('intro')}
    />
  );
};

export default ConsonantPracticePage;
